import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects, type ProjectsQueryOptions } from '../hooks/useProjects';
import { DataTable, type Column } from '../components/DataTable';
import { SavedFiltersMenu } from '../components/SavedFiltersMenu';
import { ProjectFilterSelect, WorkerFilterSelect } from '../components/EntityFilterSelect';
import { useScheduleFilterState } from '../hooks/useScheduleFilterState';
import type { ProjectListItemDto } from '../lib/types';

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString();
}

export function ProjectsPage() {
  const [options, setOptions] = useState<ProjectsQueryOptions>({ page: 1, pageSize: 25 });
  const navigate = useNavigate();
  // Narrowing the selection can shrink the result set below the current page — go back to page 1.
  const resetPage = useCallback(() => setOptions((o) => ({ ...o, page: 1 })), []);
  const { filters, changeFilters, appliedFilter, applySavedFilter, personIds, projectIds } =
    useScheduleFilterState(resetPage);
  const { data, isLoading, error } = useProjects({ ...options, personIds, projectIds });

  const columns: Column<ProjectListItemDto>[] = [
    {
      header: 'Job Number',
      sortBy: 'jobNumber',
      render: (r) => <span className="font-mono text-sm">{r.jobNumber}</span>,
    },
    {
      header: 'Project Name',
      render: (r) => <span className="max-w-xs truncate block">{r.name}</span>,
    },
    { header: 'Service Line', render: (r) => r.serviceLine ?? '—' },
    { header: 'Account', render: (r) => r.account ?? '—' },
    { header: 'Start', sortBy: 'projectedStart', render: (r) => formatDate(r.projectedStart) },
    { header: 'End', render: (r) => formatDate(r.projectedEnd) },
  ];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-brand-blue">Projects</h1>
        <p className="text-sm text-brand-charcoal/70">All active jobs</p>
      </div>
      <DataTable
        data={data}
        isLoading={isLoading}
        error={error}
        options={options}
        onOptionsChange={setOptions}
        columns={columns}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/projects/${r.jobNumber}`)}
        emptyState="No projects match your search."
        searchable
        searchPlaceholder="Search by job number or project name…"
        toolbar={
          <div className="flex items-center gap-2 flex-wrap">
            <SavedFiltersMenu
              applied={appliedFilter}
              onApply={applySavedFilter}
              currentPeople={filters.people}
              currentProjects={filters.projects}
            />
            <WorkerFilterSelect
              selected={filters.people}
              onChange={(people) => changeFilters({ ...filters, people })}
            />
            <ProjectFilterSelect
              selected={filters.projects}
              onChange={(projects) => changeFilters({ ...filters, projects })}
            />
          </div>
        }
      />
    </section>
  );
}
